/**
 * A tela do gestor: quem pode entrar, e de qual aparelho.
 *
 * As duas colunas da tela são as duas perguntas que o gestor faz na vida real,
 * e a diferença entre elas importa:
 *
 *   PESSOAS   — quem tem direito de entrar. Desligar aqui fecha TODOS os
 *               aparelhos daquela pessoa de uma vez. É o que se faz quando
 *               alguém sai da fazenda.
 *   APARELHOS — quais celulares estão reconhecidos agora. Revogar aqui fecha
 *               só aquele. É o que se faz quando um celular é perdido e a
 *               pessoa continua na equipe.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  salvarPessoa, desligarPessoa, gerarAcesso, listarAparelhos, listarPessoas,
  revogarAparelho, type AcessoGerado, type Aparelho, type Perfil, type Pessoa,
} from '../acesso/api'

const PERFIS: readonly Perfil[] = ['tratador', 'gestor', 'administrador']

export function Acessos() {
  const [pessoas, setPessoas] = useState<Pessoa[]>([])
  const [aparelhos, setAparelhos] = useState<Aparelho[]>([])
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)

  /** O código recém-gerado, que aparece uma vez e some quando o gestor fecha. */
  const [novoAcesso, setNovoAcesso] = useState<{ pessoa: Pessoa; acesso: AcessoGerado } | null>(null)
  const [editando, setEditando] = useState<string | null>(null)
  const [criando, setCriando] = useState(false)
  const [mostrarDesligadas, setMostrarDesligadas] = useState(false)

  const recarregar = useCallback(async () => {
    try {
      const [p, a] = await Promise.all([listarPessoas(), listarAparelhos()])
      setPessoas(p)
      setAparelhos(a)
      setErro(null)
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'não deu para carregar')
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    void recarregar()
  }, [recarregar])

  async function aoGerar(pessoa: Pessoa) {
    setErro(null)
    try {
      setNovoAcesso({ pessoa, acesso: await gerarAcesso(pessoa.id) })
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'não deu para gerar')
    }
  }

  async function aoAlternar(pessoa: Pessoa) {
    const desligando = pessoa.ativo
    if (desligando && !confirm(`Desligar ${pessoa.nome}? Todos os aparelhos dela param de entrar.`)) {
      return
    }
    await desligarPessoa(pessoa.id, !pessoa.ativo)
    await recarregar()
  }

  async function aoRevogar(aparelho: Aparelho) {
    if (!confirm(`Revogar ${aparelho.descricao_aparelho ?? 'este aparelho'} de ${aparelho.pessoa}?`)) {
      return
    }
    await revogarAparelho(aparelho.sessao_id, 'revogado pelo gestor')
    await recarregar()
  }

  if (carregando) return <p className="aviso">Carregando…</p>

  const ativas = pessoas.filter((p) => p.ativo)
  const desligadas = pessoas.filter((p) => !p.ativo)

  return (
    <div className="acessos">
      {erro && <p className="aviso-erro">{erro}</p>}

      {novoAcesso && (
        <CodigoGerado
          nome={novoAcesso.pessoa.nome}
          acesso={novoAcesso.acesso}
          aoFechar={() => {
            setNovoAcesso(null)
            void recarregar()
          }}
        />
      )}

      <section className="bloco">
        <h2>Pessoas</h2>
        <ul className="lista">
          {ativas.map((pessoa) =>
            editando === pessoa.id ? (
              <li key={pessoa.id} className="linha-editando">
                <FormularioDePessoa
                  pessoa={pessoa}
                  aoSalvar={() => {
                    setEditando(null)
                    void recarregar()
                  }}
                  aoCancelar={() => setEditando(null)}
                />
              </li>
            ) : (
              <LinhaDePessoa
                key={pessoa.id}
                pessoa={pessoa}
                aoGerar={() => void aoGerar(pessoa)}
                aoEditar={() => setEditando(pessoa.id)}
                aoAlternar={() => void aoAlternar(pessoa)}
              />
            ),
          )}
        </ul>

        {/* AS DESLIGADAS FICAM, MAS RECOLHIDAS.
            O gestor pediu para «limpar a tela» das pessoas desligadas, e
            apagar de verdade não é possível nem desejável: o app não tem
            DELETE em tabela nenhuma (migração 003, de propósito), e cada
            pessoa é dona dos registros que gravou — apagá-la deixaria meses
            de trato sem autor. Recolher resolve o que ele quer sem perder o
            que não se pode perder. */}
        {desligadas.length > 0 && (
          <div className="desligadas">
            <button
              type="button"
              className="discreto"
              onClick={() => setMostrarDesligadas((m) => !m)}
            >
              {mostrarDesligadas
                ? 'esconder as desligadas'
                : `${desligadas.length} ${desligadas.length === 1 ? 'pessoa desligada' : 'pessoas desligadas'} — mostrar`}
            </button>

            {mostrarDesligadas && (
              <ul className="lista">
                {desligadas.map((pessoa) =>
                  editando === pessoa.id ? (
                    <li key={pessoa.id} className="linha-editando">
                      <FormularioDePessoa
                        pessoa={pessoa}
                        aoSalvar={() => {
                          setEditando(null)
                          void recarregar()
                        }}
                        aoCancelar={() => setEditando(null)}
                      />
                    </li>
                  ) : (
                    <LinhaDePessoa
                      key={pessoa.id}
                      pessoa={pessoa}
                      aoGerar={() => void aoGerar(pessoa)}
                      aoEditar={() => setEditando(pessoa.id)}
                      aoAlternar={() => void aoAlternar(pessoa)}
                    />
                  ),
                )}
              </ul>
            )}
          </div>
        )}

        {criando ? (
          <FormularioDePessoa
            aoSalvar={() => {
              setCriando(false)
              void recarregar()
            }}
            aoCancelar={() => setCriando(false)}
          />
        ) : (
          <button type="button" className="discreto" onClick={() => setCriando(true)}>
            + Cadastrar pessoa
          </button>
        )}
      </section>

      <section className="bloco">
        <h2>Aparelhos reconhecidos</h2>
        {aparelhos.length === 0 ? (
          <p className="secundario">Nenhum aparelho entrou ainda.</p>
        ) : (
          <ul className="lista">
            {aparelhos.map((aparelho) => (
              <li key={aparelho.sessao_id} className="linha">
                <div>
                  <strong>{aparelho.pessoa}</strong>
                  <div className="secundario">
                    {aparelho.descricao_aparelho ?? 'aparelho'} · último uso{' '}
                    {quando(aparelho.ultimo_uso_em ?? aparelho.criada_em)}
                  </div>
                </div>
                <div className="acoes">
                  <button type="button" className="discreto" onClick={() => void aoRevogar(aparelho)}>
                    Revogar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

/**
 * O código aparece UMA vez. Não existe "ver de novo" — o servidor guardou só a
 * forma embaralhada e não sabe mais qual era. Perdeu, gera outro.
 */
function CodigoGerado({
  nome, acesso, aoFechar,
}: {
  nome: string
  acesso: AcessoGerado
  aoFechar: () => void
}) {
  const [copiado, setCopiado] = useState(false)

  const recado =
    `Olá, ${nome}. Seu acesso ao app da Fazenda Cunhataí:\n\n` +
    `${acesso.codigo}\n\n` +
    `Abra ${location.origin} e digite esse código. ` +
    `Vale por ${acesso.dias} dias e serve uma vez só.`

  return (
    <div className="codigo-gerado">
      <h2>Código de {nome}</h2>
      <p className="codigo">{acesso.codigo}</p>
      <p className="secundario">
        Aparece uma vez só. Copie agora — nem o sistema sabe qual é depois que
        esta caixa fechar. Vale {acesso.dias} dias e serve uma vez.
      </p>
      <div className="acoes">
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard.writeText(recado).then(() => setCopiado(true))
          }}
        >
          {copiado ? 'Copiado ✓' : 'Copiar recado do WhatsApp'}
        </button>
        <button type="button" className="discreto" onClick={aoFechar}>
          Fechar
        </button>
      </div>
    </div>
  )
}

/**
 * Uma pessoa na lista, com o que dá para fazer com ela.
 *
 * «Gerar acesso» só aparece para quem está ativa: gerar código para alguém
 * desligada produziria um código que não entra, e descobrir isso é sempre
 * pelo WhatsApp, depois, com a pessoa do outro lado tentando.
 */
function LinhaDePessoa({
  pessoa, aoGerar, aoEditar, aoAlternar,
}: {
  pessoa: Pessoa
  aoGerar: () => void
  aoEditar: () => void
  aoAlternar: () => void
}) {
  return (
    <li className={pessoa.ativo ? 'linha' : 'linha inativa'}>
      <div>
        <strong>{pessoa.nome}</strong>
        <span className="etiqueta">{pessoa.perfil}</span>
        {!pessoa.ativo && <span className="etiqueta etiqueta-fria">desligada</span>}
        {pessoa.email && <div className="secundario">{pessoa.email}</div>}
      </div>
      <div className="acoes">
        {pessoa.ativo && (
          <button type="button" onClick={aoGerar}>
            Gerar acesso
          </button>
        )}
        <button type="button" className="discreto" onClick={aoEditar}>
          editar
        </button>
        <button type="button" className="discreto" onClick={aoAlternar}>
          {pessoa.ativo ? 'Desligar' : 'Religar'}
        </button>
      </div>
    </li>
  )
}

/**
 * CADASTRAR E CORRIGIR SÃO O MESMO FORMULÁRIO.
 *
 * Sem `pessoa` ele cria; com `pessoa` ele corrige. A rota do servidor é a
 * mesma, como na tela de Tanques — dois formulários para os mesmos três
 * campos divergem no dia em que um ganha validação que o outro não tem.
 *
 * ANTES DISTO, corrigir um perfil digitado errado exigia desligar a pessoa e
 * cadastrar outra. A desligada ficava na lista para sempre, e o gestor
 * acabava com uma lista cheia de tentativas — foi exatamente o que aconteceu
 * na hora de cadastrar o peixeiro.
 *
 * Mudar o perfil NÃO MEXE NO PASSADO: os registros que a pessoa gravou
 * continuam dela. O que muda é o que ela pode fazer daqui para a frente, e
 * vale na primeira vez que o app conferir o crachá.
 */
function FormularioDePessoa({
  pessoa, aoSalvar, aoCancelar,
}: {
  pessoa?: Pessoa
  aoSalvar: () => void
  aoCancelar: () => void
}) {
  const [nome, setNome] = useState(pessoa?.nome ?? '')
  const [email, setEmail] = useState(pessoa?.email ?? '')
  const [perfil, setPerfil] = useState<Perfil>(pessoa?.perfil ?? 'tratador')
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  const corrigindo = pessoa !== undefined

  return (
    <form
      className="nova-pessoa"
      onSubmit={(evento) => {
        evento.preventDefault()
        setSalvando(true)
        setErro(null)
        salvarPessoa({
          ...(corrigindo ? { id: pessoa.id } : {}),
          nome: nome.trim(),
          email: email.trim(),
          perfil,
        })
          .then(aoSalvar)
          .catch((falha: unknown) => {
            setErro(falha instanceof Error ? falha.message : 'não deu para salvar')
          })
          .finally(() => setSalvando(false))
      }}
    >
      {corrigindo && <span className="campo-rotulo">Corrigindo o cadastro</span>}

      <label className="campo">
        <span className="campo-rotulo">Nome</span>
        <input value={nome} onChange={(e) => setNome(e.target.value)} />
      </label>
      <label className="campo">
        <span className="campo-rotulo">E-mail (opcional)</span>
        <input value={email} onChange={(e) => setEmail(e.target.value)} inputMode="email" />
      </label>
      <label className="campo">
        <span className="campo-rotulo">Perfil</span>
        <select value={perfil} onChange={(e) => setPerfil(e.target.value as Perfil)}>
          {PERFIS.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </label>

      {corrigindo && perfil !== pessoa.perfil && (
        <p className="secundario">
          O perfil muda de <strong>{pessoa.perfil}</strong> para <strong>{perfil}</strong>. O
          que ela já registrou continua sendo dela; muda o que ela vê e pode fazer daqui
          para a frente.
        </p>
      )}

      {erro && <p className="aviso-erro">{erro}</p>}

      <div className="acoes">
        <button type="submit" disabled={salvando || nome.trim().length < 2}>
          {salvando ? 'Salvando…' : corrigindo ? 'Salvar' : 'Cadastrar'}
        </button>
        <button type="button" className="discreto" onClick={aoCancelar}>
          Cancelar
        </button>
      </div>
    </form>
  )
}

function quando(iso: string): string {
  const data = new Date(iso)
  const horas = (Date.now() - data.getTime()) / 3600_000
  if (horas < 1) return 'agora há pouco'
  if (horas < 24) return 'hoje'
  return data.toLocaleDateString('pt-BR')
}
